import { create, useModal } from '@ebay/nice-modal-react';
import Button from '@shared/components/Button/Button';
import Popup from '@shared/components/Popup/Popup';
import { VerificationPhase } from 'matrix-js-sdk/src/crypto-api';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Devices } from 'assets/icons/Devices.svg';
import GradientIconWithShadow from 'components/GradientIconWithShadow/GradientIconWithShadow';
import Spinner from 'components/Spinner/Spinner';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import useMatrixDeviceVerificationContext from 'contexts/MatrixDeviceVerificationContext/useMatrixDeviceVerificationContext';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

const IncomingSasVerificationRequestModal = create(() => {
  const { t } = useTranslation();
  const [lastSeenIp, setLastSeenIp] = useState<string | null>();
  const [deviceName, setDeviceName] = useState<string | null>();
  const [location, setLocation] = useState<string | null>();
  const modal = useModal(IncomingSasVerificationRequestModal);
  const { activeRequest, getLocationFromIp } = useMatrixDeviceContext();
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });
  const { sasDecimalNumbers, isConfirming, confirmNumbersMatch, cancelVerification } =
    useMatrixDeviceVerificationContext();

  const handleThisWasntMe = async () => {
    if (matrixClient && activeRequest?.otherUserId && activeRequest?.otherDeviceId) {
      const contentMap = new Map<string, Map<string, object>>();
      contentMap.set(activeRequest.otherUserId, new Map([[activeRequest.otherDeviceId, { action: 'logout' }]]));
      await matrixClient.sendToDevice(CustomMeedioMatrixEvents.LOGOUT_DEVICE, contentMap);
    }
    cancelVerification();
    modal.remove();
  };

  useEffect(() => {
    if (!activeRequest || [VerificationPhase.Cancelled, VerificationPhase.Done].includes(activeRequest.phase)) {
      modal.remove();
    }
  }, [activeRequest, modal]);

  useEffect(() => {
    if (!activeRequest || !matrixClient) return;

    matrixClient.getDevice(activeRequest.otherDeviceId ?? '').then(async (deviceInfo) => {
      const ip = deviceInfo.last_seen_ip;
      const name = deviceInfo.display_name;
      setLastSeenIp(ip);
      setDeviceName(name);
      const loc = await getLocationFromIp(ip);
      setLocation(loc);
    });
  }, [activeRequest, matrixClient, getLocationFromIp]);

  const deviceInfoLine = deviceName;
  const ipLocationLine =
    lastSeenIp && location
      ? `${t('verification.ip_address')}: ${lastSeenIp} • ${location}`
      : lastSeenIp
      ? `${t('verification.ip_address')}: ${lastSeenIp}`
      : location;

  return (
    <Popup isVisible>
      <Popup.FullScreenContainer className="md:min-w-[512px]">
        <div className="relative flex flex-col gap-6 rounded-2xl border border-gray-30 bg-gray-10 p-6 pt-12 md:p-6">
          <div className="flex flex-col items-center gap-8">
            <GradientIconWithShadow
              className="backdrop-blur-[20px] border border-white/40"
              icon={Devices}
              iconVariant="primary"
              bgVariant="primary"
            />
            <div className="flex flex-col gap-2 text-center">
              <h2 className="text-xl font-semibold text-gray-100">{t('verification.login_from_new_device')}</h2>
              {deviceInfoLine && <p className="text-base text-gray-80">{deviceInfoLine}</p>}
              {ipLocationLine && <p className="text-base text-gray-80">{ipLocationLine}</p>}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="rounded-2xl border border-gray-30 bg-white p-4">
              {sasDecimalNumbers ? (
                <div className="flex flex-col gap-2">
                  <p className="text-center text-[40px] font-semibold tracking-[4px] leading-[1.25] text-gray-100">
                    {sasDecimalNumbers}
                  </p>
                  <p className="text-center text-sm text-gray-100">
                    {t('verification.check_codes_match_on_both_devices')}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Spinner variant="gray" size="xs" />
                  <p className="text-center text-sm text-gray-80">{t('verification.waiting_for_verification')}</p>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-4">
            <Button
              variant="secondaryTertiary"
              onClick={handleThisWasntMe}
              disabled={isConfirming}
              className="flex-1 !bg-gray-10 hover:!bg-white"
            >
              {t('verification.this_wasnt_me')}
            </Button>
            <Button
              variant="primary"
              onClick={confirmNumbersMatch}
              disabled={isConfirming}
              loading={!sasDecimalNumbers}
              className="flex-1"
            >
              {isConfirming ? t('confirming') : t('verification.confirm_device')}
            </Button>
          </div>
          <p className="text-center text-xs text-gray-80">{t('verification.security_warning_unrecognized_request')}</p>
        </div>
      </Popup.FullScreenContainer>
    </Popup>
  );
});

export default IncomingSasVerificationRequestModal;
